import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useStore } from "../store";
import * as api from "../api";
import type { TrainingJob, TrainingMetrics, HyperParams } from "../types";
import TaskAuthor from "./TaskAuthor";

/* ------------------------------------------------------------------ */
/*  Status badge helper                                                */
/* ------------------------------------------------------------------ */


function fmtMetric(value: unknown, digits: number, suffix = ""): string {
  return typeof value === "number" && Number.isFinite(value)
    ? value.toFixed(digits) + suffix
    : "--";
}

function statusBadgeClass(status: TrainingJob["status"]): string {
  switch (status) {
    case "completed":
      return "badge-success";
    case "running":
      return "badge-info";
    case "failed":
      return "badge-danger";
    case "queued":
      return "badge-warning";
    case "stopped":
      return "badge-neutral";
    default:
      return "badge-neutral";
  }
}

/* ------------------------------------------------------------------ */
/*  Simple bar chart for metrics history                               */
/* ------------------------------------------------------------------ */

function MetricsChart({
  values,
  label,
}: {
  values: number[];
  label: string;
}) {
  if (values.length === 0) return null;

  const maxVal = Math.max(...values, 0.001);
  const minVal = Math.min(...values, 0);
  const range = maxVal - minVal || 1;
  const width = 400;
  const height = 120;
  const padding = { top: 10, right: 10, bottom: 20, left: 40 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const points = values.map((v, i) => {
    const x = padding.left + (i / Math.max(values.length - 1, 1)) * chartW;
    const y = padding.top + chartH - ((v - minVal) / range) * chartH;
    return { x, y, v };
  });

  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
    .join(" ");

  const yTicks = [minVal, (minVal + maxVal) / 2, maxVal];

  return (
    <div>
      <div
        className="text-sm text-muted"
        style={{ marginBottom: 6, fontWeight: 600 }}
      >
        {label}
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: "100%", maxWidth: width, height: "auto" }}
      >
        {yTicks.map((tick, i) => {
          const y =
            padding.top + chartH - ((tick - minVal) / range) * chartH;
          return (
            <g key={i}>
              <line
                x1={padding.left}
                y1={y}
                x2={padding.left + chartW}
                y2={y}
                stroke="var(--color-border, #e0e0e0)"
                strokeDasharray="2,2"
              />
              <text
                x={padding.left - 4}
                y={y + 3}
                textAnchor="end"
                fontSize="9"
                fill="var(--color-text-muted, #999)"
              >
                {tick.toFixed(tick < 1 ? 3 : 1)}
              </text>
            </g>
          );
        })}
        <path
          d={linePath}
          fill="none"
          stroke="var(--color-primary, #3b82f6)"
          strokeWidth="2"
        />
        {points.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r="3"
            fill="var(--color-primary, #3b82f6)"
          >
            <title>
              Step {i + 1}: {p.v.toFixed(4)}
            </title>
          </circle>
        ))}
        <text
          x={padding.left + chartW / 2}
          y={height - 2}
          textAnchor="middle"
          fontSize="9"
          fill="var(--color-text-muted, #999)"
        >
          Step
        </text>
      </svg>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const MODEL_TYPES = ["PPO", "SAC", "TD3", "DreamerV3", "World Model"] as const;
const SCENARIOS = ["Reach", "Pick", "Place", "Pick & Place", "Push", "Stack"] as const;

const DEFAULT_HYPERPARAMS: HyperParams = {
  learning_rate: 0.0003,
  batch_size: 256,
  max_steps: 100_000,
  discount_factor: 0.99,
  entropy_coeff: 0.01,
};

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function TrainingManager() {
  const { id: projectId } = useParams<{ id: string }>();
  const trainingJobs = useStore((s) => s.trainingJobs);
  const environments = useStore((s) => s.environments);
  const loading = useStore((s) => s.loading);
  const error = useStore((s) => s.error);
  const clearError = useStore((s) => s.clearError);
  const fetchTrainingJobs = useStore((s) => s.fetchTrainingJobs);
  const fetchEnvironments = useStore((s) => s.fetchEnvironments);
  const stopTrainingJob = useStore((s) => s.stopTrainingJob);

  /* form state */
  const [jobName, setJobName] = useState("");
  const [modelType, setModelType] = useState<string>(MODEL_TYPES[0]);
  const [scenario, setScenario] = useState<string>(SCENARIOS[0]);
  const [environmentId, setEnvironmentId] = useState("");
  const [learningRate, setLearningRate] = useState(
    String(DEFAULT_HYPERPARAMS.learning_rate),
  );
  const [maxSteps, setMaxSteps] = useState(
    String(DEFAULT_HYPERPARAMS.max_steps),
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  /* LLM-authored task */
  const [authoredReward, setAuthoredReward] = useState<{
    taskSpec: any;
    rewardSource: string;
  } | null>(null);

  /* selected job for metrics */
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  useEffect(() => {
    if (projectId) {
      fetchTrainingJobs(projectId);
      fetchEnvironments(projectId);
    }
  }, [projectId, fetchTrainingJobs, fetchEnvironments]);

  // Auto-select first environment when list loads
  useEffect(() => {
    if (!environmentId && environments.length > 0) {
      setEnvironmentId(environments[0].id);
    }
  }, [environments, environmentId]);

  // Poll running jobs for updated metrics
  useEffect(() => {
    const runningJobs = trainingJobs.filter((j) => j.status === "running");
    if (runningJobs.length === 0 || !projectId) return;

    const interval = setInterval(() => {
      fetchTrainingJobs(projectId);
    }, 5000);

    return () => clearInterval(interval);
  }, [trainingJobs, projectId, fetchTrainingJobs]);

  const handleStartTraining = useCallback(async () => {
    if (!projectId || !jobName.trim() || !environmentId) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const hyperparams: HyperParams = {
        ...DEFAULT_HYPERPARAMS,
        learning_rate: parseFloat(learningRate) || DEFAULT_HYPERPARAMS.learning_rate,
        max_steps: parseInt(maxSteps, 10) || DEFAULT_HYPERPARAMS.max_steps,
      };

      await api.startTraining(projectId, {
        name: jobName.trim(),
        environment_id: environmentId,
        model_type: modelType,
        scenario,
        hyperparams,
        ...(authoredReward ? { reward_source: authoredReward.rewardSource } : {}),
      } as any);

      // Refresh job list and reset form
      await fetchTrainingJobs(projectId);
      setJobName("");
      setLearningRate(String(DEFAULT_HYPERPARAMS.learning_rate));
      setMaxSteps(String(DEFAULT_HYPERPARAMS.max_steps));
    } catch (e) {
      setSubmitError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }, [
    projectId,
    jobName,
    environmentId,
    modelType,
    scenario,
    learningRate,
    maxSteps,
    fetchTrainingJobs,
  ]);

  const handleStopJob = useCallback(
    async (jobId: string) => {
      if (!projectId) return;
      await stopTrainingJob(projectId, jobId);
    },
    [projectId, stopTrainingJob],
  );

  const selectedJob = trainingJobs.find((j) => j.id === selectedJobId);
  const selectedMetrics: TrainingMetrics | null =
    selectedJob?.metrics ?? null;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Training Manager</h1>
          <p className="page-subtitle">
            Configure and monitor training jobs for this project
          </p>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div
          className="card mb-4"
          style={{ borderColor: "var(--color-danger)" }}
        >
          <div className="flex justify-between items-center">
            <span style={{ color: "var(--color-danger)" }}>{error}</span>
            <button className="btn btn-sm btn-secondary" onClick={clearError}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Task authoring via natural language */}
      <TaskAuthor onTaskAuthored={setAuthoredReward} />

      {authoredReward && (
        <div style={{ marginTop: 8, marginBottom: 8 }}>
          <span className="badge badge-success">
            Using LLM-authored task: {authoredReward.taskSpec?.task_type ?? "custom"}
          </span>
        </div>
      )}

      {/* New training job form */}
      <div className="card mb-4">
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
          New Training Job
        </h2>

        {submitError && (
          <div
            style={{
              color: "var(--color-danger)",
              fontSize: 13,
              marginBottom: 12,
            }}
          >
            {submitError}
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div className="form-group">
            <label className="form-label" htmlFor="job-name">
              Job Name
            </label>
            <input
              id="job-name"
              className="form-input"
              type="text"
              value={jobName}
              onChange={(e) => setJobName(e.target.value)}
              placeholder="e.g., Pick-Red-Box-v1"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="environment-select">
              Environment
            </label>
            <select
              id="environment-select"
              className="form-select"
              value={environmentId}
              onChange={(e) => setEnvironmentId(e.target.value)}
            >
              {environments.length === 0 && (
                <option value="">No environments available</option>
              )}
              {environments
                .filter((env) => env.status === "ready")
                .map((env) => (
                  <option key={env.id} value={env.id}>
                    {env.name}
                  </option>
                ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="model-type">
              Model Type
            </label>
            <select
              id="model-type"
              className="form-select"
              value={modelType}
              onChange={(e) => setModelType(e.target.value)}
            >
              {MODEL_TYPES.map((mt) => (
                <option key={mt} value={mt}>
                  {mt}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="scenario-select">
              Scenario
            </label>
            <select
              id="scenario-select"
              className="form-select"
              value={scenario}
              onChange={(e) => setScenario(e.target.value)}
            >
              {SCENARIOS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="learning-rate">
              Learning Rate
            </label>
            <input
              id="learning-rate"
              className="form-input"
              type="number"
              step="0.0001"
              min="0"
              value={learningRate}
              onChange={(e) => setLearningRate(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="max-steps">
              Max Steps (Epochs)
            </label>
            <input
              id="max-steps"
              className="form-input"
              type="number"
              step="1000"
              min="1000"
              value={maxSteps}
              onChange={(e) => setMaxSteps(e.target.value)}
            />
          </div>
        </div>

        <div style={{ marginTop: 8 }}>
          <button
            className="btn btn-primary"
            onClick={handleStartTraining}
            disabled={submitting || !jobName.trim() || !environmentId}
          >
            {submitting ? "Starting..." : "Start Training"}
          </button>
        </div>
      </div>

      {/* Training jobs list */}
      <div className="card">
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
          Training Jobs
        </h2>

        {loading && trainingJobs.length === 0 ? (
          <p className="text-muted">Loading training jobs...</p>
        ) : trainingJobs.length === 0 ? (
          <p className="text-muted">
            No training jobs yet. Configure and start one above.
          </p>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Model</th>
                  <th>Scenario</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Loss</th>
                  <th>Reward</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {trainingJobs.map((job) => {
                  const metrics = job.metrics;
                  const progressPct =
                    metrics && metrics.total_steps > 0
                      ? Math.round(
                          (metrics.step / metrics.total_steps) * 100,
                        )
                      : 0;

                  return (
                    <tr
                      key={job.id}
                      style={{
                        cursor: "pointer",
                        background:
                          selectedJobId === job.id
                            ? "var(--color-accent-muted)"
                            : undefined,
                      }}
                      onClick={() =>
                        setSelectedJobId(
                          selectedJobId === job.id ? null : job.id,
                        )
                      }
                    >
                      <td style={{ fontWeight: 500 }}>{job.name}</td>
                      <td>{job.model_type}</td>
                      <td>{job.scenario}</td>
                      <td>
                        <span className={`badge ${statusBadgeClass(job.status)}`}>
                          {job.status}
                        </span>
                      </td>
                      <td style={{ minWidth: 120 }}>
                        {job.status === "running" && metrics ? (
                          <div>
                            <div className="progress-bar">
                              <div
                                className="progress-bar-fill"
                                style={{ width: `${progressPct}%` }}
                              />
                            </div>
                            <span className="text-sm text-muted">
                              {(metrics.step ?? 0).toLocaleString()} /{" "}
                              {(metrics.total_steps ?? 0).toLocaleString()}
                            </span>
                          </div>
                        ) : job.status === "completed" ? (
                          <span className="text-sm" style={{ color: "var(--color-success)" }}>
                            100%
                          </span>
                        ) : (
                          <span className="text-sm text-muted">--</span>
                        )}
                      </td>
                      <td>
                        {metrics ? (
                          <span className="text-sm">
                            {fmtMetric(metrics.loss, 4)}
                          </span>
                        ) : (
                          <span className="text-sm text-muted">--</span>
                        )}
                      </td>
                      <td>
                        {metrics ? (
                          <span className="text-sm">
                            {fmtMetric(metrics.reward_mean, 2)}
                          </span>
                        ) : (
                          <span className="text-sm text-muted">--</span>
                        )}
                      </td>
                      <td>
                        {(job.status === "running" ||
                          job.status === "queued") && (
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStopJob(job.id);
                            }}
                          >
                            Stop
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Metrics detail for selected job */}
      {selectedJob && selectedMetrics && (
        <div className="card" style={{ marginTop: 16 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
            Metrics: {selectedJob.name}
          </h2>

          <div className="stat-row" style={{ marginBottom: 16 }}>
            <div className="stat-card">
              <div className="stat-value">
                {fmtMetric(selectedMetrics.loss, 4)}
              </div>
              <div className="stat-label">Loss</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {fmtMetric(selectedMetrics.reward_mean, 2)}
              </div>
              <div className="stat-label">Mean Reward</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {fmtMetric(typeof selectedMetrics.success_rate === "number" ? selectedMetrics.success_rate * 100 : undefined, 1, "%")}
              </div>
              <div className="stat-label">Success Rate</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {fmtMetric(selectedMetrics.episode_length_mean, 0)}
              </div>
              <div className="stat-label">Avg Episode Length</div>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
            }}
          >
            <MetricsChart
              values={selectedMetrics.loss_history ?? []}
              label="Loss History"
            />
            <MetricsChart
              values={selectedMetrics.reward_history ?? []}
              label="Reward History"
            />
          </div>
        </div>
      )}
    </div>
  );
}
